/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

// import { Control, useController, useFieldArray, useWatch } from 'react-hook-form'
import { Collapse, Stack, styled, useMediaQuery, useTheme } from "@mui/material";
import _ from "lodash";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import { Button } from "../../../components/Button";
import { QuantityChip } from "../../../components/QuantityChip";
import Typography from "../../../components/Typography";
import { useAppDispatch } from "../../../store/rootStore";
import TestIds from "../../../testIds";
import { error } from "../../../util/log";
import { selectCurrentFarmerAccount, selectHomeBranch } from "../../azure-b2c/storage";
import { Product } from "../../products/storage";
import { StocksSlice } from "../../products/storage/stocks.slice";
import { fetchProductDetail } from "../../products/utils/fetchProductDetail";
import { locationToSiteId } from "../../stores/storage/utils";
import { useAddSeedModal } from "../components/AddSeedModal";
import { ExpandIcon } from "../components/ExpandIcon";
import { setSeedValue } from "../storage/seedCart.slice";
import {
  ProductQuantityDefaultValue,
  SeedMix,
  SeedMixSchema,
  SeedProductLine,
  SeedTypesenseProduct,
} from "../types/SeedOrderFormType";
import { allPaddocksHectares, allSeedsCount, allSeedsKg, roundedTo } from "../utils/calculateTotal";

import { ProductRow } from "./ProductRow";

const SeedProductsTitleBox = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    justifyContent: "flex-start",
  },
}));

export const SeedProductForm = ({
  seedMix,
  formInvalidated,
}: {
  seedMix: SeedMix;
  formInvalidated?: boolean;
}) => {
  const account = useSelector(selectCurrentFarmerAccount);
  const dispatch = useAppDispatch();
  const { setOpen, setCallback, setSeedType, setSeedId } = useAddSeedModal();

  const showSeedProduct = seedMix.productsExpanded;

  const setShowSeedProduct = (show: boolean) => {
    const accountNumber = account?.accountNumber;
    if (accountNumber) {
      dispatch(
        setSeedValue({
          accountNumber,
          key: "productsExpanded",
          value: show,
          seedMix,
        })
      );
    }
  };

  const paddocks = seedMix.paddocks;
  const seeds = seedMix.products;

  const totalKgs = useMemo(() => allSeedsKg(seeds), [seeds]);
  const totalSeeds = useMemo(() => allSeedsCount(seeds), [seeds]);

  const validation = SeedMixSchema.safeParse(seedMix);
  const productsError = validation.error?.formErrors?.fieldErrors?.products?.[0];
  const productFields = seedMix.products;

  const appendProduct = (product: SeedProductLine) => {
    const accountNumber = account?.accountNumber;
    if (accountNumber) {
      const copy = _.cloneDeep(seedMix);
      copy.products.push(product);
      dispatch(
        setSeedValue({
          accountNumber,
          key: "",
          value: copy,
          seedMix,
        })
      );
    }
  };

  const removeProduct = (index: number) => {
    const accountNumber = account?.accountNumber;
    if (accountNumber) {
      const copy = _.cloneDeep(seedMix);
      if (index >= 0 && index < copy.products.length) {
        copy.products.splice(index, 1);
        dispatch(
          setSeedValue({
            accountNumber,
            key: "",
            value: copy,
            seedMix,
          })
        );
      } else {
        error("Invalid index: Unable to remove product");
      }
    }
  };

  const homeBranchId = useSelector(selectHomeBranch);

  const addProduct = () => {
    setSeedType(seedMix.mixType);
    if (seedMix.id) {
      setSeedId(seedMix.id);
    }

    setCallback(() => async (item: Product) => {
      const branchId = homeBranchId ? locationToSiteId(homeBranchId) : homeBranchId;
      try {
        const newItem = await fetchProductDetail(
          item.masterProductId.toString(),
          account?.accountNumber,
          branchId
        );
        await dispatch(StocksSlice.actions.addStock(newItem));
        setOpen(false);
        let sowingRate = 1;
        const seedItem = newItem as unknown as SeedTypesenseProduct;
        if (seedItem.sowing_rate !== undefined) {
          sowingRate = roundedTo(seedItem.sowing_rate);
        }

        const quantity = allPaddocksHectares(paddocks) * sowingRate;
        const roundedUp = quantity % 1 !== 0;

        appendProduct({
          ...ProductQuantityDefaultValue,
          sowingRate,
          productId: item.productId,
          quantity: Math.ceil(quantity),
          quantityRoundedUp: roundedUp,
        });
      } catch (err) {
        error("Error fetching product details:", err);
      }
    });
    setOpen(true);
  };

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Stack px={2} py={3}>
      <SeedProductsTitleBox>
        <Stack direction="row" width="100%" justifyContent={"space-between"} gap={2}>
          <Stack
            direction={isSm ? "column" : "row"}
            spacing={3}
            justifyContent={"space-between"}
            width={isSm ? "auto" : "100%"}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" fontWeight="700" fontSize="20px">
                Seeds
              </Typography>
              <QuantityChip label={`${totalSeeds} products`} />
              <QuantityChip label={`${totalKgs}kg`} />
            </Stack>
            <Button
              variant="outlined"
              onClick={addProduct}
              sx={{ width: "fit-content" }}
              data-testid={TestIds.feat.seedOrders.straightOrPremixed.addSeeds}
            >
              Add seeds
            </Button>
          </Stack>
          <Stack direction="row" alignItems={isSm ? "flex-start" : "center"}>
            {showSeedProduct ? (
              <ExpandIcon icon="expand_less" onClick={() => setShowSeedProduct(false)} />
            ) : (
              <ExpandIcon icon="expand_more" onClick={() => setShowSeedProduct(true)} />
            )}
          </Stack>
        </Stack>
        <Collapse in={showSeedProduct} sx={{ width: "100%" }}>
          <Stack gap={2} mt={3}>
            {productFields.map((field, index) => {
              return (
                <ProductRow
                  // control={control}
                  index={index}
                  key={field.lineId}
                  remove={removeProduct}
                  seedMix={seedMix}
                />
              );
            })}
          </Stack>
        </Collapse>
        {formInvalidated && productsError && (
          <Typography
            variant="body2"
            color="error700"
            mt={0.5}
            data-testid={TestIds.feat.seed.product.notEnoughProductsError}
          >
            {productsError}
          </Typography>
        )}
      </SeedProductsTitleBox>
    </Stack>
  );
};
